// src/pages/auth/verify.js
import React, { useEffect, useState, useContext } from "react";
import { useRouter } from "./../util/router";
import { useAuth } from "./../util/auth";
import { requireAuth } from "./../util/auth";
import { sendEmailVerification } from "firebase/auth";
import { updateUser, createLocationGroup, createLocation } from "./../util/db";
import { getPendingSignup, deletePendingSignup, createProduction } from "./../util/apiHelpers";
import { Button, Box, CircularProgress, Alert } from "@mui/material";
import { toast } from 'react-hot-toast';
import { createInitialLocationFromSignUpAddress } from "../util/createInitialLocationFromSignUpAddress";
import { DateContext } from "../providers/DateProvider";

function VerifyEmailPage(props) {
  const auth = useAuth();
  const router = useRouter();
  const [signupData, setSignupData] = useState(null); // { user, productionData }
  const [pending, setPending] = useState(false);
  const [formAlert, setFormAlert] = useState(null);
  const { dayStamp } = useContext(DateContext);

  // Load pending signup from Firestore on mount
  useEffect(() => {
    const fetchPending = async () => {
      if (!auth.user) return;
      try {
        // 1️⃣ Mobile-created user → skip pending signup entirely
        if (auth.user.createdFromMobile) {
          console.log('This user was created from the mobile app, and doesnt have the production data yet. After verification, well kick them over to the etner-show-info page to get it before we send them to the dashboard.')
          setSignupData({ user: auth.user });
          return;
        }
        // 1️⃣ Check if existing account has a productionId but email not verified
        if (auth.user.productionId && !auth.rawUser?.emailVerified) {
            setSignupData({ user: auth.user });
            return; // stop further execution        
        }
      
        // 2️⃣ Otherwise, check pending signup in Firestore
        const pending = await getPendingSignup(auth.user.uid);

        //dont think we need this check anymore after router addition
        // if (!pending && !auth.user) {
        //   // No pending signup → fallback to signup page
        //   // router.replace("/auth/signup");
        //   return;
        // }

        if (!pending && auth.user && auth.user.emailVerified) {
            router.replace("/dashboard");
            return;
          }
          
        console.log("Pending from Firestore:", pending);
        setSignupData({ user: auth.user, productionData: pending.productionData });
      } catch (error) {
        console.error(error);
      }
    };

    fetchPending();
  }, [auth.rawUser?.emailVerified, auth.user, router]);

  // Poll for email verification
  useEffect(() => {
    if (!auth.user) return; // Only poll if user exists
  
    let intervalId = null;
  
    const startPolling = () => {
      intervalId = setInterval(async () => {
        try {
          await auth.rawUser?.reload();
          if (auth.rawUser?.emailVerified) {
            clearInterval(intervalId);
            await handleEmailVerified();
          }
        } catch (err) {
          console.error("Polling error:", err);
        }
      }, 2000);
    };
  
    startPolling();
  
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [auth.user, signupData]);

  const resendVerification = async () => {
    if (!auth.user) return;
    setPending(true);
    try {
      await sendEmailVerification(auth.rawUser);
      toast.success(`Verification email sent to ${auth.user.email}!`);
    //   setFormAlert({ type: "success", message: "Verification email sent!" });
    } catch (error) {
      console.error(error);
      toast.error(`Error: ${error.message}`);
    //   setFormAlert({ type: "error", message: error.message });
    } finally {
      setPending(false);
    }
  };

  const handleEmailVerified = async () => {
    if (!signupData) return;
    const { user, productionData } = signupData;

    console.log(user)

    try {
      // 1️⃣ If user came from mobile → skip pending signup flow
      if (user.createdFromMobile) {
        router.replace("/enter-show-info");
        return;
      }


      // 2️⃣ If production → create production and update profile
      if (user.userType === 'production') {
        const productionRef = await createProduction(user.uid, productionData);

        await auth.updateProfile({productionId: productionRef.productionId, productionName: productionData.productionName,businessName: productionData.businessName});

        // get coords from the sign up data production address to preload one production location on initial sign up to give user an example.
        await createInitialLocationFromSignUpAddress(productionRef, productionData, dayStamp);
    }

      await deletePendingSignup(user.uid);
      router.replace("/dashboard");



    } catch (error) {
      console.error(error);
      setFormAlert({ type: "error", message: error.message });
    }
  };

  if (!signupData) {
    return (
      <Box textAlign="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box textAlign="center" p={4}>
      {formAlert && <Alert severity={formAlert.type}>{formAlert.message}</Alert>}

      <h2>Verify your email</h2>
      <p>We sent a verification link to your email address. Click the link in that email to activate your account. </p>
      <p style={{textTransform: 'uppercase', fontWeight: 'bold', color: '#ffd600ff'}}>*Please check your spam folder if you do not see it in your inbox!</p>
      <Button variant="contained" onClick={resendVerification} disabled={pending}>
        {pending ? "Sending..." : "Resend Verification Email"}
      </Button>
    </Box>
  );
}

export default requireAuth(VerifyEmailPage);
